import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowButton,
  Badge,
  BadgeDot,
  Dot,
  DotsWrap,
  HeaderDivider,
  HeaderStat,
  HeaderStatLabel,
  HeaderStatValue,
  HeaderWrap,
  IndicatorLabel,
  MarketplaceCard,
  MarketplaceGrid,
  MainContainer,
  NavButtons,
  PlanCard,
  PlanFeature,
  PlanName,
  PlanPrice,
  PlanPriceUnit,
  PlanTag,
  PrimaryBtn,
  ProductCard,
  ProductCardBadge,
  ProductCardEmoji,
  ProductCardName,
  ProductCards,
  ScoreBar,
  ScoreBarFill,
  ScoreCard,
  ScoreRow,
  ScoreValue,
  SecondaryBtn,
  Slide,
  SlideContent,
  SlideDescription,
  SlideLeft,
  SlideRight,
  SlideTitle,
  SlidesTrack,
  SliderWrap,
  WelcomeAvatar,
  WelcomeGreeting,
  WelcomeLeft,
  WelcomeName,
  WelcomeRight,
  WelcomeText,
} from "./styles";
import cache from "utils/cache";
import { useVerifyUserQuery } from "api/authApi";
import { useGetHeaderStatsQuery } from "api/dashboardApi";

const AUTOPLAY_MS = 5500;

const slides = [
  { theme: "dark", key: "trending" },
  { theme: "light", key: "supplier" },
  { theme: "dark", key: "platform" },
  { theme: "light", key: "upgrade" },
];

const HeroBannerCarousel = ({ onViewAllCategories }) => {
  const navigate = useNavigate();
  const { data: verifiedUser } = useVerifyUserQuery();
  const user = verifiedUser || cache.getItem("user");
  const [current, setCurrent] = useState(0);

  const { data: headerStats, isFetching: statsLoading } = useGetHeaderStatsQuery(
    undefined,
    { skip: !user?.id }
  );

  const firstName = user?.firstName || "User";
  const avatarInitial = firstName?.[0]?.toUpperCase() || "U";
  const dateLabel = useMemo(
    () =>
      new Date().toLocaleDateString("en-US", {
        weekday: "long",
        day: "2-digit",
        month: "short",
        year: "numeric",
      }),
    []
  );

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % slides.length);
    }, AUTOPLAY_MS);
    return () => clearInterval(timer);
  }, []);

  const isDarkSlide = slides[current].theme === "dark";

  const goTo = (index) => setCurrent((index + slides.length) % slides.length);

  const ordersTodayDisplay =
    statsLoading && user?.id ? "…" : headerStats?.ordersToday ?? "—";
  const revenueDisplay =
    statsLoading && user?.id
      ? "…"
      : headerStats?.revenueThisMonth != null
        ? `$${Number(headerStats.revenueThisMonth).toLocaleString("en-US", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })}`
        : "—";
  const fulfillmentDisplay =
    statsLoading && user?.id
      ? "…"
      : headerStats?.fulfillmentRate != null
        ? `${headerStats.fulfillmentRate}%`
        : "—";

  return (
    <MainContainer>
      <HeaderWrap>
        <WelcomeLeft>
          <WelcomeAvatar>{avatarInitial}</WelcomeAvatar>
          <WelcomeText>
            <WelcomeGreeting>Good morning</WelcomeGreeting>
            <WelcomeName>
              Welcome Back, <span>{firstName}</span>
            </WelcomeName>
          </WelcomeText>
        </WelcomeLeft>
        <WelcomeRight>
          <HeaderStat>
            <HeaderStatValue className="orange">{ordersTodayDisplay}</HeaderStatValue>
            <HeaderStatLabel>Orders Today</HeaderStatLabel>
          </HeaderStat>
          <HeaderDivider />
          <HeaderStat>
            <HeaderStatValue className="green">{revenueDisplay}</HeaderStatValue>
            <HeaderStatLabel>This Month</HeaderStatLabel>
          </HeaderStat>
          <HeaderDivider />
          <HeaderStat>
            <HeaderStatValue>{fulfillmentDisplay}</HeaderStatValue>
            <HeaderStatLabel>Fulfillment Rate</HeaderStatLabel>
          </HeaderStat>
          <HeaderDivider />
          <IndicatorLabel>{dateLabel}</IndicatorLabel>
        </WelcomeRight>
      </HeaderWrap>

      <SliderWrap>
        <SlidesTrack style={{ transform: `translateX(-${current * 25}%)` }}>
          <Slide variant="dark">
            <SlideContent>
              <SlideLeft>
                <Badge variant="orange">
                  <BadgeDot /> Updated Weekly
                </Badge>
                <SlideTitle variant="dark">
                  This week&apos;s <span className="orange">top sellers</span>
                  <br />
                  ready to import.
                </SlideTitle>
                <SlideDescription>
                  Our AI has curated the best-performing products across Fashion, Electronics and
                  Home. One click to push directly to your Shopify or eBay store.
                </SlideDescription>
                <PrimaryBtn type="button" onClick={() => navigate("/best-selling-products")}>
                  Browse Trending Products -&gt;
                </PrimaryBtn>
                <SecondaryBtn
                  type="button"
                  variant="dark"
                  onClick={() => onViewAllCategories?.()}
                >
                  View All Categories
                </SecondaryBtn>
              </SlideLeft>
              <SlideRight>
                <ProductCards>
                  <ProductCard>
                    <ProductCardEmoji>👟</ProductCardEmoji>
                    <ProductCardName>Running Shoes Pro</ProductCardName>
                    <ProductCardBadge>Hot</ProductCardBadge>
                  </ProductCard>
                  <ProductCard featured>
                    <ProductCardEmoji>📱</ProductCardEmoji>
                    <ProductCardName>Wireless Earbuds V3</ProductCardName>
                    <ProductCardBadge>Top 1</ProductCardBadge>
                  </ProductCard>
                  <ProductCard>
                    <ProductCardEmoji>💄</ProductCardEmoji>
                    <ProductCardName>Glow Face Serum</ProductCardName>
                    <ProductCardBadge>Trending</ProductCardBadge>
                  </ProductCard>
                </ProductCards>
              </SlideRight>
            </SlideContent>
          </Slide>

          <Slide variant="light">
            <SlideContent>
              <SlideLeft>
                <Badge variant="green">Weekly Report</Badge>
                <SlideTitle variant="light">
                  Your suppliers are <span className="green">performing well</span> this week.
                </SlideTitle>
                <SlideDescription>
                  Reliable suppliers means better reviews, fewer disputes, and happier customers.
                  Track supplier health in real time from your dashboard.
                </SlideDescription>
                <PrimaryBtn type="button" className="green" onClick={() => navigate("/supplier-dashboard")}>
                  View Supplier Dashboard -&gt;
                </PrimaryBtn>
                <SecondaryBtn type="button" onClick={() => navigate("/my-orders")}>
                  See All Orders
                </SecondaryBtn>
              </SlideLeft>
              <SlideRight>
                <ScoreCard>
                  <ScoreRow>
                    <span>On-time shipping</span>
                    <ScoreValue className="green">96%</ScoreValue>
                  </ScoreRow>
                  <ScoreBar>
                    <ScoreBarFill width="96%" className="green" />
                  </ScoreBar>
                  <ScoreRow>
                    <span>Tracking accuracy</span>
                    <ScoreValue className="green">98%</ScoreValue>
                  </ScoreRow>
                  <ScoreBar>
                    <ScoreBarFill width="98%" className="green" />
                  </ScoreBar>
                  <ScoreRow>
                    <span>Inventory accuracy</span>
                    <ScoreValue className="orange">87%</ScoreValue>
                  </ScoreRow>
                  <ScoreBar>
                    <ScoreBarFill width="87%" className="orange" />
                  </ScoreBar>
                </ScoreCard>
              </SlideRight>
            </SlideContent>
          </Slide>

          <Slide variant="darkBlue">
            <SlideContent>
              <SlideLeft>
                <Badge variant="blue">
                  <BadgeDot className="blue" /> Platform Update
                </Badge>
                <SlideTitle variant="dark">
                  Now selling on <span className="blue">all 5 marketplaces.</span>
                </SlideTitle>
                <SlideDescription>
                  Fully integrate with Shopify, eBay, Amazon, TikTok Shop and Walmart. Connect all
                  your stores and manage everything from one dashboard.
                </SlideDescription>
                <PrimaryBtn type="button" className="blue" onClick={() => navigate("/profile")}>
                  Connect Your Stores -&gt;
                </PrimaryBtn>
                <SecondaryBtn type="button" variant="dark" onClick={() => navigate("/about")}>
                  Learn More
                </SecondaryBtn>
              </SlideLeft>
              <SlideRight>
                <MarketplaceGrid>
                  <MarketplaceCard className="shopify">Shopify</MarketplaceCard>
                  <MarketplaceCard className="ebay">eBay</MarketplaceCard>
                  <MarketplaceCard className="amazon">Amazon</MarketplaceCard>
                  <MarketplaceCard className="tiktok">TikTok Shop</MarketplaceCard>
                </MarketplaceGrid>
              </SlideRight>
            </SlideContent>
          </Slide>

          <Slide variant="lightGreen">
            <SlideContent>
              <SlideLeft>
                <Badge variant="green">Unlock More</Badge>
                <SlideTitle variant="light">
                  Scale faster with <span className="green">Pro features.</span>
                </SlideTitle>
                <SlideDescription>
                  Upgrade to Pro and unlock unlimited imports, priority supplier access, wholesale
                  pricing, and advanced analytics.
                </SlideDescription>
                <PrimaryBtn type="button" className="green" onClick={() => navigate("/pricing")}>
                  Upgrade to Pro -&gt;
                </PrimaryBtn>
                <SecondaryBtn type="button" onClick={() => navigate("/pricing")}>
                  See What&apos;s Included
                </SecondaryBtn>
              </SlideLeft>
              <SlideRight>
                <PlanCard>
                  <PlanName>
                    Pro Plan <PlanTag>Most Popular</PlanTag>
                  </PlanName>
                  <PlanPrice>
                    $49.99 <PlanPriceUnit>/ month</PlanPriceUnit>
                  </PlanPrice>
                  <PlanFeature>Unlimited product imports</PlanFeature>
                  <PlanFeature>All 5 marketplace integrations</PlanFeature>
                  <PlanFeature>Wholesale pricing on 1M+ products</PlanFeature>
                </PlanCard>
              </SlideRight>
            </SlideContent>
          </Slide>
        </SlidesTrack>

        <NavButtons>
          <ArrowButton
            type="button"
            $dark={isDarkSlide}
            onClick={() => goTo(current - 1)}
            aria-label="Previous slide"
          >
            ‹
          </ArrowButton>
          <ArrowButton
            type="button"
            $dark={isDarkSlide}
            onClick={() => goTo(current + 1)}
            aria-label="Next slide"
          >
            ›
          </ArrowButton>
        </NavButtons>

        <DotsWrap>
          {slides.map((slide, idx) => (
            <Dot
              type="button"
              key={slide.key}
              $active={idx === current}
              $dark={isDarkSlide}
              onClick={() => goTo(idx)}
            />
          ))}
        </DotsWrap>
      </SliderWrap>
    </MainContainer>
  );
};

export default HeroBannerCarousel;
